// Reprise de la lecture audio au PREMIER GESTE de l'utilisateur — CCT-226.
//
// ⚠️ UN `play()` REFUSÉ N'EST PAS UNE PANNE, C'EST UNE RÈGLE DU NAVIGATEUR. Sans geste
// préalable, `HTMLMediaElement.play()` rejette avec `NotAllowedError` : « play() failed because
// the user didn't interact with the document first ». C'est le cas à CHAQUE chargement de page
// — F5, lien direct, retour depuis un autre onglet.
//
// ⚠️ ET LES DEUX HOOKS AVALAIENT CE REJET DANS UN `console.warn`. Mesuré en navigateur, sans
// l'option `--autoplay-policy=no-user-gesture-required` : 2 appels à `play()`, 2 rejets, ZÉRO
// erreur visible. La musique et les voix ne partaient jamais, et rien à l'écran ne le disait.
// Pire côté répliques : `useLineAudio` marquait la ligne EN ÉCHEC, si bien que la scène
// enchaînait sans la voix — un refus d'autoplay était traité comme un fichier manquant.
//
// La parade est celle de tous les lecteurs média : réessayer au premier geste. L'utilisateur
// clique quelque part — n'importe où — et le son démarre.

/** Le navigateur a-t-il refusé la lecture faute de geste ? (par opposition à un vrai échec) */
export function estRefusAutoplay(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name;
  // ⚠️ ON TESTE `name`, PAS LE MESSAGE. Le message est traduit dans la langue du navigateur —
  // s'y fier ferait passer un refus pour une panne chez tout utilisateur non anglophone,
  // c'est-à-dire précisément le public de ce produit.
  return name === 'NotAllowedError';
}

const GESTES = ['pointerdown', 'keydown', 'touchstart'] as const;

/**
 * Rejoue `reprendre` au premier geste de l'utilisateur, une seule fois.
 *
 * Renvoie une fonction d'annulation — indispensable : sans elle, un écran démonté laisserait
 * des écouteurs qui relanceraient l'audio d'une scène qu'on a quittée.
 */
export function reprendreAuPremierGeste(reprendre: () => void): () => void {
  if (typeof document === 'undefined') return () => {};
  let arme = true;
  const surGeste = () => {
    if (!arme) return;
    arme = false;
    retirer();
    try { reprendre(); } catch { /* best-effort : un échec ici ne doit rien casser */ }
  };
  const retirer = () => {
    for (const g of GESTES) document.removeEventListener(g, surGeste, true);
  };
  for (const g of GESTES) document.addEventListener(g, surGeste, true);
  return () => { arme = false; retirer(); };
}
